import React, { useContext, useState } from 'react';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertMessage } from '@eposnow/ui-core';
import FormikTextField from '../../components/formikComponents/FormikTextField';
import { ReactComponent as PlusIcon } from '../../img/small-plus-icon.svg';
import ActionsContainer from '../../components/ActionsContainer';
import { checkAndFormatCountry, locationV1toV4 } from '../../helpers/helpers';
import { locations1validationSchema } from './validationSchemas';
import { StepContext } from '../../context/StepContext';
import { PageMetadata } from '../../components/datagridComponents/paginationHelpers';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import { LocationV1, LocationV4 } from '../../types';
import Loader from '../../components/Loader';
import { UserContext } from '../../context/UserContext';
import AddressFormWithGoogleAutocomplete from '../../components/AddressFormWithGoogleAutocomplete';
import useCountries from '../../helpers/useCountries';

const LocationsMainAddressForm = ({
  setIsInFirstPage,
}: {
  setIsInFirstPage: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { locale } = useContext(UserContext);
  const { countries } = useCountries();
  const { apiFetch } = useContext(ApiContext);
  const { goToPreviousStep } = useContext(StepContext);
  const [showAddressLine2, setShowAddressLine2] = useState(false);
  const [showAddressDescription, setShowAddressDescription] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: addresses, isLoading } = useQuery<{ data: LocationV1[]; metadata: PageMetadata }>(
    'addresses',
    () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location`, false),
    {
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error fetching address'), {
          extra: { error: JSON.stringify(error) },
        });
      },
    }
  );

  const { isLoading: isUpdating, mutate: updateAddresses } = useMutation(
    (address: LocationV4) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location`,
        address,
        'PUT'
      ),
    {
      async onSuccess() {
        await queryClient.invalidateQueries('addresses');
        setIsInFirstPage(false);
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error updating address'), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorMessage(t('errors.errorSavingData'));
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      ...addresses?.data?.[0],
      name: addresses?.data?.[0]?.name || '',
      description: addresses?.data?.[0]?.description || '',
      addressLine1: addresses?.data?.[0]?.address1 || '',
      address2: addresses?.data?.[0]?.address2 || '',
      town: addresses?.data?.[0]?.town || '',
      county: addresses?.data?.[0]?.county || '',
      postCode: addresses?.data?.[0]?.postCode || '',
      country: addresses?.data?.[0]?.countryName || '',
    },
    validationSchema: locations1validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const requestValues = locationV1toV4({
        ...values,
        address1: values.addressLine1,
        countryName: checkAndFormatCountry(values.country, countries),
      });
      updateAddresses(requestValues);
    },
  });

  return isLoading || isUpdating ? (
    <Loader ariaBusy={isLoading || isUpdating} />
  ) : (
    <>
      {errorMessage && (
        <AlertMessage
          type="error"
          styles={{ marginBottom: 2 }}
          action={() => setErrorMessage('')}
          iconAlignment="baseline"
          locale={locale}
          theme={theme}
        >
          {errorMessage}
        </AlertMessage>
      )}

      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '1.5rem',
          marginBottom: '3rem',
        }}
      >
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="h2">{t('screens.locations1.title')}</Typography>

              <Box sx={{ marginTop: '1.5rem' }}>
                <FormikTextField
                  label={t('screens.locations1.locationName')}
                  id="name"
                  name="name"
                  formName="wizardLocations1"
                  required
                  helperText={t('screens.locations1.locationNameHelper')}
                />

                {showAddressDescription || formik?.values?.description ? (
                  <FormikTextField
                    label={t('screens.locations1.description')}
                    id="description"
                    name="description"
                    formName="wizardLocations1"
                  />
                ) : (
                  <Button
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'left',
                      justifyContent: 'flex-start',
                      padding: 0,
                      marginBottom: '1.5rem',
                      width: '100%',
                      minHeight: '44px',
                      minWidth: '44px',
                    }}
                    onClick={() => {
                      setShowAddressDescription(true);
                    }}
                  >
                    <PlusIcon />
                    <Typography
                      variant="body1"
                      sx={{ marginLeft: '0.5rem', color: 'primary.main', fontWeight: '600' }}
                    >
                      {t('screens.locations1.addDescription')}
                    </Typography>
                  </Button>
                )}
                <AddressFormWithGoogleAutocomplete
                  formik={formik}
                  formName="wizardLocations1"
                  translationKey="screens.locations1"
                  extraAddressLine={
                    showAddressLine2 || formik?.values?.address2 ? (
                      <FormikTextField
                        label={t('screens.locations1.line2')}
                        id="address2"
                        name="address2"
                        formName="wizardLocations1"
                      />
                    ) : (
                      <Button
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'left',
                          justifyContent: 'flex-start',
                          padding: 0,
                          marginBottom: '1.5rem',
                          width: '100%',
                          minHeight: '44px',
                          minWidth: '44px',
                        }}
                        onClick={() => {
                          setShowAddressLine2(true);
                        }}
                      >
                        <PlusIcon />
                        <Typography
                          variant="body1"
                          sx={{ marginLeft: '0.5rem', color: 'primary.main', fontWeight: '600' }}
                        >
                          {t('screens.personalInformation.addAddressLine2')}
                        </Typography>
                      </Button>
                    )
                  }
                />
              </Box>
            </Box>

            <ActionsContainer>
              <Button
                variant="outlined"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={goToPreviousStep}
              >
                {t('actions.previous')}
              </Button>

              <Button
                variant="contained"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                type="submit"
              >
                {t('actions.next')}
              </Button>
            </ActionsContainer>
          </Form>
        </FormikProvider>
      </Card>
    </>
  );
};

export default LocationsMainAddressForm;
